import { useCallback, useRef, useState } from 'react';
import type React from 'react';

const DRAG_THRESHOLD_PX = 6;

type DragOrigin = {
  pointerId: number;
  x: number;
  scrollOffset: number;
  viewport: HTMLElement;
};

/** Mouse dragging scrolls projects; the release click must not select or open one. */
export const useProjectDragScroll = ({
  axis = 'x',
  scrollContainerSelector,
}: {
  axis?: 'x' | 'y';
  scrollContainerSelector?: string;
} = {}) => {
  const origin = useRef<DragOrigin | undefined>(undefined);
  const suppressClick = useRef(false);
  const [dragging, setDragging] = useState(false);

  const onPointerDown = useCallback<React.PointerEventHandler<HTMLDivElement>>(
    (event) => {
      suppressClick.current = false;
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (
        scrollContainerSelector &&
        event.target instanceof Element &&
        event.target.closest(
          'button, a, textarea, select, input:not([readonly]), [contenteditable="true"]',
        )
      )
        return;
      const viewport = scrollContainerSelector
        ? event.currentTarget.closest<HTMLElement>(scrollContainerSelector)
        : event.currentTarget;
      if (!viewport) return;
      // Leave the native scrollbar thumb to the browser.
      if (
        event.clientY >=
        viewport.getBoundingClientRect().top + viewport.clientHeight
      )
        return;
      origin.current = {
        pointerId: event.pointerId,
        x: axis === 'x' ? event.clientX : event.clientY,
        scrollOffset: axis === 'x' ? viewport.scrollLeft : viewport.scrollTop,
        viewport,
      };
    },
    [axis, scrollContainerSelector],
  );

  const onPointerMove = useCallback<React.PointerEventHandler<HTMLDivElement>>(
    (event) => {
      const start = origin.current;
      if (!start || start.pointerId !== event.pointerId) return;
      if (event.buttons !== 1) {
        origin.current = undefined;
        setDragging(false);
        return;
      }
      const delta = (axis === 'x' ? event.clientX : event.clientY) - start.x;
      if (!suppressClick.current && Math.abs(delta) < DRAG_THRESHOLD_PX) return;
      if (!suppressClick.current) {
        suppressClick.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
        setDragging(true);
      }
      event.preventDefault();
      if (axis === 'x') start.viewport.scrollLeft = start.scrollOffset - delta;
      else start.viewport.scrollTop = start.scrollOffset - delta;
    },
    [axis],
  );

  const onPointerUp = useCallback<React.PointerEventHandler<HTMLDivElement>>(
    (event) => {
      if (origin.current?.pointerId !== event.pointerId) return;
      origin.current = undefined;
      setDragging(false);
    },
    [],
  );

  const onPointerCancel = useCallback<
    React.PointerEventHandler<HTMLDivElement>
  >(() => {
    origin.current = undefined;
    suppressClick.current = false;
    setDragging(false);
  }, []);

  const onClickCapture = useCallback<React.MouseEventHandler<HTMLDivElement>>(
    (event) => {
      if (!suppressClick.current || event.detail === 0) return;
      suppressClick.current = false;
      event.preventDefault();
      event.stopPropagation();
    },
    [],
  );

  return {
    dragging,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onLostPointerCapture: onPointerUp,
    onClickCapture,
  };
};
